import { useState } from "react"
import { Link } from "react-router"
import { ArrowLeft, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Separator } from "@/components/ui/separator"
import { useUsageStats, type RecentLog } from '@/lib/hooks'

type Period = "7d" | "30d" | "all"

const periodLabels: Record<Period, string> = {
  "7d": "7日",
  "30d": "30日",
  all: "全期間",
}

function tryFormatJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}

function CollapsibleSection({
  title,
  content,
  formatJson = false,
}: {
  title: string
  content: string | null
  formatJson?: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  if (!content) {
    return (
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">データなし</p>
      </div>
    )
  }

  const displayContent = formatJson ? tryFormatJson(content) : content

  return (
    <div className="space-y-1">
      <button
        className="flex w-full items-center justify-between text-sm font-medium text-muted-foreground"
        onClick={() => setExpanded(!expanded)}
      >
        {title}
        <ChevronDown
          className={`size-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <pre className="max-h-60 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-all">
          {displayContent}
        </pre>
      )}
    </div>
  )
}

export default function ApiUsagePage() {
  const [period, setPeriod] = useState<Period>("30d")
  const [selectedLog, setSelectedLog] = useState<RecentLog | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const { data: stats = null, isLoading: loading } = useUsageStats(period)

  const maxCost = stats
    ? Math.max(...stats.daily.map((d) => d.cost_usd), 0.000001)
    : 0

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-lg px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Link to="/settings" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">AI解析履歴</h1>
        </div>

        {/* Period toggle */}
        <div className="mb-4 flex gap-2">
          {(Object.keys(periodLabels) as Period[]).map((p) => (
            <Button
              key={p}
              variant={period === p ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(p)}
            >
              {periodLabels[p]}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {/* Summary cards skeleton */}
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="py-0">
                  <CardContent className="px-3 py-3">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="mt-2 h-6 w-12" />
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Daily chart skeleton */}
            <Card>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-28" />
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1" style={{ height: 120 }}>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center gap-1">
                      <Skeleton
                        className="w-full rounded-t"
                        style={{ height: `${20 + Math.random() * 80}%` }}
                      />
                      <Skeleton className="h-2 w-6" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            {/* Table skeleton */}
            <Card>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-20" />
              </CardHeader>
              <CardContent className="px-0">
                <div className="space-y-0">
                  <div className="flex border-b px-4 py-2">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="ml-4 h-3 w-24" />
                    <Skeleton className="ml-auto h-3 w-12" />
                    <Skeleton className="ml-4 h-3 w-12" />
                  </div>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex border-b px-4 py-2 last:border-0">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="ml-4 h-3 w-20" />
                      <Skeleton className="ml-auto h-3 w-10" />
                      <Skeleton className="ml-4 h-3 w-10" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : !stats || stats.total_calls === 0 ? (
          <p className="text-sm text-muted-foreground">
            データがありません
          </p>
        ) : (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="py-0">
                <CardContent className="px-3 py-3">
                  <p className="text-xs text-muted-foreground">呼び出し回数</p>
                  <p className="text-lg font-semibold">
                    {stats.total_calls}
                  </p>
                </CardContent>
              </Card>
              <Card className="py-0">
                <CardContent className="px-3 py-3">
                  <p className="text-xs text-muted-foreground">トークン数</p>
                  <p className="text-lg font-semibold">
                    {(
                      stats.total_input_tokens + stats.total_output_tokens
                    ).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card className="py-0">
                <CardContent className="px-3 py-3">
                  <p className="text-xs text-muted-foreground">コスト</p>
                  <p className="text-lg font-semibold">
                    ${stats.total_cost_usd.toFixed(4)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Daily bar chart */}
            {stats.daily.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">日別コスト (USD)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="-mx-6 overflow-x-auto px-6">
                    <div
                      className="flex items-end gap-1"
                      style={{
                        height: 120,
                        minWidth: `${stats.daily.length * 24}px`,
                      }}
                    >
                      {stats.daily.map((d) => {
                        const pct = (d.cost_usd / maxCost) * 100
                        return (
                          <div
                            key={d.date}
                            className="group relative flex flex-1 flex-col items-center"
                            style={{ height: "100%" }}
                          >
                            <div className="flex w-full flex-1 items-end">
                              <div
                                className="w-full rounded-t bg-muted-foreground/40"
                                style={{
                                  height: `${Math.max(pct, 2)}%`,
                                }}
                              />
                            </div>
                            <span className="mt-1 text-[9px] text-muted-foreground">
                              {d.date.slice(5)}
                            </span>
                            {/* Tooltip */}
                            <div className="pointer-events-none absolute -top-10 left-1/2 z-10 hidden -translate-x-1/2 rounded border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md group-hover:block">
                              <div>{d.date}</div>
                              <div>{d.calls}回 / ${d.cost_usd.toFixed(4)}</div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent logs table */}
            {stats.recent.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">呼び出し履歴</CardTitle>
                </CardHeader>
                <CardContent className="px-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="px-4 py-2">日時</th>
                          <th className="px-4 py-2">エンドポイント</th>
                          <th className="px-4 py-2 text-right">トークン</th>
                          <th className="px-4 py-2 text-right">コスト</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent.map((log) => (
                          <tr
                            key={log.id}
                            className="border-b last:border-0 cursor-pointer active:bg-muted/50 transition-colors"
                            onClick={() => {
                              setSelectedLog(log)
                              setSheetOpen(true)
                            }}
                          >
                            <td className="px-4 py-2 whitespace-nowrap">
                              {new Date(log.created_at).toLocaleDateString(
                                "ja-JP",
                                {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </td>
                            <td className="px-4 py-2">{log.endpoint}</td>
                            <td className="px-4 py-2 text-right">
                              {(
                                log.input_tokens + log.output_tokens
                              ).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right">
                              ${log.cost_usd.toFixed(4)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Usage Log Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl max-h-[85vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>API呼び出し詳細</SheetTitle>
            <SheetDescription className="sr-only">
              API呼び出しの詳細情報
            </SheetDescription>
          </SheetHeader>

          {selectedLog && (
            <div className="space-y-4 px-4 pb-8">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">日時</span>
                  <span>
                    {new Date(selectedLog.created_at).toLocaleDateString("ja-JP", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">エンドポイント</span>
                  <span>{selectedLog.endpoint}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">モデル</span>
                  <span className="font-mono text-xs">{selectedLog.model}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">入力トークン</span>
                  <span>{selectedLog.input_tokens.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">出力トークン</span>
                  <span>{selectedLog.output_tokens.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">コスト</span>
                  <span>${selectedLog.cost_usd.toFixed(6)}</span>
                </div>
              </div>

              <Separator />

              <CollapsibleSection title="プロンプト" content={selectedLog.request_body} />
              <CollapsibleSection title="レスポンス" content={selectedLog.response_body} formatJson />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
